import { User } from '@sr-sdks/api-sdk-axios';
import { useEffect, useRef } from 'react';
import { setUserProperties } from '../utils/analytics/dataLayer';
import {
  AnalyticsIdentity,
  UNSET_IDENTITY,
  serialiseIdentity,
} from '../utils/analytics/identity';
import { isExternalUser } from '../utils/helpers/common';
import { useCurrentUserAuth } from './useCurrentUserAuth';

/**
 * Derives the GA4-safe identity for a user, or `null` if there isn't one yet.
 *
 * `user_id` is deliberately `user._id` -- the PERSON's id. GA4 resolves
 * identity per person, and the launch scorecard's Adoption and Retention
 * rows count people: a member with two workspaces must still report as one
 * user, and two teammates sharing one workspace must not collapse into one.
 * Do NOT swap this for `workspace._id` or a membership id.
 */
export const deriveAnalyticsIdentity = (
  user?: User,
): AnalyticsIdentity | null => {
  if (!user?._id) return null;

  return {
    is_external: isExternalUser(user),
    user_id: user._id,
  };
};

/**
 * Pushes the current user's identity into the GTM dataLayer as user-scoped
 * properties (no events). Recomputes whenever the derived identity changes
 * (login, user switch) and explicitly unsets it when the user goes away.
 *
 * Call this once, high enough in the tree to cover every authenticated
 * route (see App.tsx) -- not per layout or page.
 */
export const useAnalyticsIdentity = (): void => {
  const { currentUser } = useCurrentUserAuth();
  // Serialised form of the last identity we pushed, so re-renders that
  // don't actually change the identity don't re-push it every time.
  const pushedRef = useRef<null | string>(null);

  useEffect(() => {
    const identity = deriveAnalyticsIdentity(currentUser);
    const serialised = serialiseIdentity(identity);

    if (serialised === pushedRef.current) return;
    pushedRef.current = serialised;

    setUserProperties(identity ?? UNSET_IDENTITY);
  }, [currentUser]);
};
